import {
  canonicalGlobalAgentId,
  GLOBAL_AGENTS_SID,
  isGlobalAgentId,
} from "@app/types/assistant/assistant";

/** Internal evaluation variants are never discoverable or directly selectable.
 * Resolvers were removed after the read-only persisted-reference audit. Stale links still fall back to Azimut. */
export function isInternalEvaluationGlobalAgent(sId: string): boolean {
  const canonical = canonicalGlobalAgentId(sId);
  return canonical.startsWith("dust-");
}

// This is the list of global agents that we want to support in past conversations but we don't want
// to be accessible to users moving forward.
const RETIRED_GLOBAL_AGENTS_SID = [
  GLOBAL_AGENTS_SID.CLAUDE_4_5_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_4_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_3_7_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_3_HAIKU,
  GLOBAL_AGENTS_SID.CLAUDE_3_OPUS,
  GLOBAL_AGENTS_SID.CLAUDE_3_SONNET,
  GLOBAL_AGENTS_SID.GITHUB,
  GLOBAL_AGENTS_SID.GOOGLE_DRIVE,
  GLOBAL_AGENTS_SID.GPT35_TURBO,
  GLOBAL_AGENTS_SID.INTERCOM,
  GLOBAL_AGENTS_SID.MISTRAL_MEDIUM,
  GLOBAL_AGENTS_SID.MISTRAL_SMALL,
  GLOBAL_AGENTS_SID.NOTION,
  GLOBAL_AGENTS_SID.O1,
  GLOBAL_AGENTS_SID.O1_HIGH_REASONING,
  GLOBAL_AGENTS_SID.O1_MINI,
  GLOBAL_AGENTS_SID.O3,
  GLOBAL_AGENTS_SID.O3_MINI,
  GLOBAL_AGENTS_SID.GPT4,
  GLOBAL_AGENTS_SID.SLACK,
  // Hidden helper sub-agent, only invoked via run_agent by deep-dive
  GLOBAL_AGENTS_SID.DUST_TASK,
  GLOBAL_AGENTS_SID.DUST_BROWSER_SUMMARY,
  GLOBAL_AGENTS_SID.DUST_PLANNING,
];

// Retired global agents remain resolvable internally (to keep past conversations running) but
// must not be surfaced to users/integrations. `getGlobalAgents` already filters them out of list
// views; callers that fetch a specific agent by sId should use this to gate the public response.
export function isRetiredGlobalAgent(sId: string): boolean {
  return (
    isInternalEvaluationGlobalAgent(sId) ||
    (isGlobalAgentId(sId) &&
      RETIRED_GLOBAL_AGENTS_SID.includes(
        canonicalGlobalAgentId(sId) as GLOBAL_AGENTS_SID
      ))
  );
}

const MODEL_ONLY_GLOBAL_AGENTS_SID: readonly GLOBAL_AGENTS_SID[] = [
  GLOBAL_AGENTS_SID.GPT35_TURBO,
  GLOBAL_AGENTS_SID.GPT4,
  GLOBAL_AGENTS_SID.GPT5,
  GLOBAL_AGENTS_SID.GPT5_THINKING,
  GLOBAL_AGENTS_SID.GPT5_NANO,
  GLOBAL_AGENTS_SID.GPT5_MINI,
  GLOBAL_AGENTS_SID.O1,
  GLOBAL_AGENTS_SID.O1_MINI,
  GLOBAL_AGENTS_SID.O1_HIGH_REASONING,
  GLOBAL_AGENTS_SID.O3_MINI,
  GLOBAL_AGENTS_SID.O3,
  GLOBAL_AGENTS_SID.CLAUDE_5_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_4_5_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_4_5_HAIKU,
  GLOBAL_AGENTS_SID.CLAUDE_4_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_3_OPUS,
  GLOBAL_AGENTS_SID.CLAUDE_3_SONNET,
  GLOBAL_AGENTS_SID.CLAUDE_3_HAIKU,
  GLOBAL_AGENTS_SID.CLAUDE_3_7_SONNET,
  GLOBAL_AGENTS_SID.MISTRAL_LARGE,
  GLOBAL_AGENTS_SID.MISTRAL_MEDIUM,
  GLOBAL_AGENTS_SID.MISTRAL_SMALL,
  GLOBAL_AGENTS_SID.GEMINI_PRO,
];

/**
 * @cc [owner:sfriquet,label:product] default-global-agent-ids
 * Lists the global agents fetched when no sIds are requested, before any workspace or caller
 * filtering: every `GLOBAL_AGENTS_SID` except retired, sidekick, reinforcement and model-only
 * agents. The result MUST NOT depend on any workspace, plan, flag or caller state.
 */
export function listDefaultGlobalAgentIds(): GLOBAL_AGENTS_SID[] {
  return (
    Object.values(GLOBAL_AGENTS_SID)
      .filter((sId) => !isRetiredGlobalAgent(sId))
      // We only want to fetch sidekick global agents if explicitly requested.
      .filter((sId) => sId !== GLOBAL_AGENTS_SID.SIDEKICK)
      // The reinforcement agent is never called directly, it is only used as a
      // placeholder when building reinforcement conversations.
      .filter((sId) => sId !== GLOBAL_AGENTS_SID.REINFORCEMENT)
      .filter((sId) => !MODEL_ONLY_GLOBAL_AGENTS_SID.includes(sId))
  );
}
